// eslint-disable-next-line no-restricted-imports -- ArcGIS draws on a canvas, where a CSS variable does not resolve
import { grey } from "@flat-ui";
import { MAP_MARKER_COLOR, MAP_MARKER_HIGHLIGHT_COLOR } from "@components/world-map/world-map-constants";

/**
 * Supercluster tuning. One config has to serve workspaces with twenty projects
 * and workspaces with twenty thousand, so density is left to the algorithm
 * rather than hand-tuned per workspace. The values differ from the library
 * defaults (radius 40, extent 512, maxZoom 16) on purpose: they are expressed
 * in ArcGIS's 256px tile scale, see each constant below.
 */

/** Cluster grouping distance, in screen pixels. Comfortably wider than the bubble. */
export const WORLD_MAP_CLUSTER_RADIUS_PX = 80;

/**
 * Supercluster measures its radius against a tile of this size, so it also defines
 * the zoom scale: the world is `extent * 2^zoom` pixels wide. ArcGIS basemaps use
 * 256px tiles, so matching that makes both the radius and the zoom line up with
 * `view.zoom` instead of being off by a factor of two.
 */
export const WORLD_MAP_CLUSTER_TILE_EXTENT = 256;

/**
 * Above this zoom every point is drawn individually, even when two projects share
 * the same coordinates and their pins overlap. Customers often create one project
 * per building of a large plant, so the pins separate at the first zoom where single
 * buildings can be told apart. Kept below the map's own maximum so a cluster always
 * has room to break apart before the user runs out of zoom.
 */
export const WORLD_MAP_CLUSTER_MAX_ZOOM = 14;

/**
 * Space kept free on each side of the view when a clicked cluster's members are
 * framed, so pins and bubbles at the edge are not cut off. A pin is about 30px tall.
 */
export const WORLD_MAP_CLUSTER_FIT_PADDING_PX = 48;

/**
 * Longest the flight into a clicked cluster may take. ArcGIS picks ~2s on its
 * own, which feels sluggish for a short hop; shorter trips stay shorter.
 */
export const WORLD_MAP_CLUSTER_ZOOM_MAX_DURATION_MS = 800;

/** Fewest points that may form a cluster. */
export const WORLD_MAP_CLUSTER_MIN_POINTS = 2;

/** Bubble and count size for clusters of at least `minCount` members. */

/**
 * Bubble sizes by member count, largest first. Fixed steps rather than sizes
 * relative to the clusters on screen, so a bubble keeps its size while the user
 * pans and zooms. The steps follow the label width: one digit, two digits, and
 * three or more characters (e.g. `1.3k`). The smallest matches the pin height.
 */
export const WORLD_MAP_CLUSTER_SIZE_STEPS = [{
  minCount: 100,
  size: 32,
  textSize: 11
}, {
  minCount: 10,
  size: 28,
  textSize: 10
}, {
  minCount: 0,
  size: 24,
  textSize: 9
}];

/** Thickness of the ring separating the bubble from the basemap, in points. */
export const WORLD_MAP_CLUSTER_OUTLINE_SIZE = 1;

/** Colour of the bubble outline. Dark, like the outline of the individual pins. */
export const WORLD_MAP_CLUSTER_OUTLINE_COLOR = grey[900];

/** Fill of the cluster bubble. Matches the individual marker colour. */
export const WORLD_MAP_CLUSTER_COLOR = MAP_MARKER_COLOR;

/** Fill of a hovered or highlighted cluster bubble. Matches the highlighted marker colour. */
export const WORLD_MAP_CLUSTER_HIGHLIGHT_COLOR = MAP_MARKER_HIGHLIGHT_COLOR;

/** Colour of the cluster count. */
export const WORLD_MAP_CLUSTER_CONTRAST_COLOR = grey[0];

/**
 * At or below this zoom the whole globe (or enough of it that `view.extent`
 * stops being meaningful) is on screen, so the viewport query falls back to the
 * whole world. Over-including clusters is invisible to the user; under-including
 * silently drops markers, so the fallback is deliberately generous.
 */
export const WORLD_MAP_CLUSTER_WORLD_BBOX_MAX_ZOOM = 5;

/**
 * Fraction of the viewport span added to each side of the queried bbox, so
 * clusters just off-screen are already formed when they pan into view.
 */
export const WORLD_MAP_CLUSTER_BBOX_PADDING_RATIO = 0.25;

/**
 * Tilt is clamped to this before deriving extra padding. `view.extent` describes
 * only the nadir footprint and does not grow when the camera tilts, so padding
 * has to stand in for the ground that becomes visible towards the horizon — and
 * that reach runs away to infinity as the tilt approaches 90 degrees.
 */
export const WORLD_MAP_CLUSTER_MAX_PADDING_TILT_DEG = 75;

/**
 * Latitude limit of the cluster queries: the geographic one, since projects can
 * sit anywhere up to the poles. Not the ~85° Web Mercator display limit:
 * supercluster pins every point past it to the projection edge, so a query
 * clamped there would never return projects closer to a pole.
 */
export const WORLD_MAP_MAX_LATITUDE = 90;

/** Westernmost longitude. */
export const WORLD_MAP_MIN_LONGITUDE = -180;

/** Easternmost longitude. */
export const WORLD_MAP_MAX_LONGITUDE = 180;

/** Longitude span of the whole globe. */
export const WORLD_MAP_LONGITUDE_SPAN = 360;

/** Decimal places a bbox is rounded to when keyed for change detection. */
export const WORLD_MAP_CLUSTER_BBOX_KEY_PRECISION = 4;